<template>
	<transition name="fade">
		<div class="zw-payment-collection" v-if="subscription" v-cloak>
			<div class="ui zw-payment segment" :class="{ 'loading' : showLoader }" @click="loadPaymentInfo">
				<div class="status">
					<!-- Logic to set status -->
					<a :class="[statusClass, 'ui empty circular label']"/>
				</div>

				<div class="date">
					<div class="ui mini statistic">
						<div class="value">
							<!-- Logic to set day -->
							{{ getTransactionDate }}
						</div>
						<div class="label">
							<!-- Logic to set month -->
							{{ getTransactionMonth }}
						</div>
						<div class="label">
							{{ getTransactionYear }}
						</div>
					</div>
				</div>

				<div class="merchant">
					<h3 class="ui header">
						{{ getSubscriptionName }}
            <span v-if="transaction.payment_purpose === 'overdue_fee'">
              ({{ $t('components.transaction.info.overdue_fee') }})
            </span>
            <div class="overdue_fee" v-if="transaction.status === 'overdue' && subscription.product.overdue_fee_amount !== null && subscription.apply_overdue_fee" style="color: orange; font-size: 1rem;">
              {{ $t('components.transaction.info.overdue_fee') }}: {{ formatPrice(subscription.product.overdue_fee_amount) }} DKK
            </div>
						<div class="sub header">
							{{ statusMessage }}
						</div>
					</h3>
				</div>

				<div class="amount">
					<h3 class="ui header">
            <span v-if="this.transaction.status === 'refunded'">
              - {{ formatPrice(amount) }}
            </span>
            <span v-else>
              {{ formatPrice(amount) }}
            </span>
						<div class="sub header">
							{{ getCurrency }}
						</div>
					</h3>
				</div>
			</div>
			<div class="ui padded segment zw-payment-info" v-if="showPaymentInfo">
				<div class="ui two column stackable grid">
					<div class="column">
						<h3 class="ui header">
							{{ $t('components.transaction.info.paymentinfo') }}
						</h3>
						<div class="content">
							<span v-if="transaction.status === 'success' || transaction.status === 'pending'">
								{{ $t('components.transaction.info.paid', {date: getDateString, time: getTimeString}) }}
							</span>
              <span v-else-if="transaction.status === 'refunded'">
                {{ $t('components.transaction.info.refunded', {date: getDateString, time: getTimeString}) }}
              </span>
              <span v-else-if="transaction.status === 'refunding'">
                {{ $t('components.transaction.info.refunding') }}
              </span>
							<span v-else-if="transaction.status === 'failed' || transaction.status === 'soft_overdue' || transaction.status === 'overdue'">
								{{ $t('components.transaction.info.tried', {date: getDateString, time: getTimeString}) }}
                <br>
                {{ $t('components.transaction.info.fail_reason', {reason: getFailTransactionReason}) }}
							</span>
						</div>

						<div class="ui header" v-if="showDownloadPdf">
							<a href="javascript:void(0)" @click="downloadInvoice()">
								Download pdf
							</a>
						</div>

						<div class="ui header" v-if="selectedAccessories.length > 0">
							{{ $t('components.transaction.info.accessories') }}
						</div>
						<div class="content" v-if="selectedAccessories.length > 0">
							<div class="ui green label" v-for="accessory in selectedAccessories" :key="accessory.id">{{ accessory.name }}</div>
						</div>
					</div>

					<div class="column">
						<h3 class="ui header">
							{{ $t('components.transaction.info.merchantinfo') }}
						</h3>

						<div class="content">
							<div class="ui list">
								<div class="item">{{ activeMerchant.name }}</div>
								<div class="item"><a :href="'tel:' + activeMerchant.phone">{{ activeMerchant.phone }}</a></div>
								<div class="item" style="word-break: break-all;">{{ activeMerchant.email }}</div>
							</div>
						</div>

						<h3 class="ui header">
							{{ $t('components.transaction.info.purchase') }}
						</h3>

						<div class="content">
              <template v-if="transaction.payment_purpose === 'overdue_fee'">
                <div class="ui two column grid">
                  <div class="left aligned column">
                    {{ $t('components.transaction.info.overdue_fee') }}
                  </div>
                  <div class="right aligned column">
                    {{ formatPrice(transaction.amount) }} {{ getCurrency }}
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="ui two column grid">
                  <div class="left aligned column">
                    {{ subscription.product.name }}
                  </div>
                  <div class="right aligned column">
                    {{ getProductPriceFromTotalPrice(subscription, transaction, amount) }}
                    <template v-if="hasTransactionKamstrupUsage"> / m<sup>3</sup></template>
                    {{ getCurrency }}
                  </div>
                </div>
                <div class="ui two column grid" style="margin-top: -2rem;" v-if="subscription.product.apply_vat">
                  <div class="left aligned column">
                    25,00% moms:
                  </div>
                  <div class="right aligned column">
                    {{ getProductPriceVatFromTotalPrice(subscription, transaction, amount) }}
                    <template v-if="hasTransactionKamstrupUsage"> / m<sup>3</sup></template>
                    {{ getCurrency }}
                  </div>
                </div>
                <template v-if="selectedAccessories.length > 0">
                  <div v-for="accessory in selectedAccessories" :key="accessory.id">
                    <div class="ui two column grid">
                      <div class="left aligned column">
                        {{ accessory.name }}
                      </div>
                      <div class="right aligned column">
                        {{ getAccessoryPrice(accessory) }}
                        <template v-if="hasTransactionKamstrupUsage && accessory.water_tax"> / m<sup>3</sup></template>
                        {{ getCurrency }}
                      </div>
                    </div>
                    <div class="ui two column grid" style="margin-top: -2rem;" v-if="accessory.apply_vat">
                      <div class="left aligned column">
                        25,00% moms:
                      </div>
                      <div class="right aligned column">
                        {{ getAccessoryPriceVat(accessory) }}
                        <template v-if="hasTransactionKamstrupUsage && accessory.water_tax"> / m<sup>3</sup></template>
                        {{ getCurrency }}
                      </div>
                    </div>
                  </div>
                </template>
                <div class="ui two column grid" v-if="subscription.expire_date">
                  <div class="left aligned column" style="color:green">
                    {{ $t('components.transaction.info.expire_date') }}
                  </div>
                  <div class="right aligned column" style="color:green">
                    {{ getExpireDate }}
                  </div>
                </div>
                <div class="ui two column grid">
                  <div class="two wide left aligned column"></div>
                  <div class="fourteen wide column">
                    <table class="ui very basic borderless compact table" v-if="false">
                      <tbody>
                      <tr v-for="accessory in selectedAccessories" :key="accessory.id">
                        <td class="left aligned">{{ accessory.name }}</td>
                        <td class="right aligned">{{ formatPrice(accessory.pricing.price) }} {{accessory.pricing.currency}}</td>
                      </tr>
                      <tr>
                        <td class="left aligned">{{ subscription.product.name }}</td>
                        <td class="right aligned">{{ formatPrice(subscription.pricing.price) }} {{ getCurrency }}</td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
                <div class="ui two column grid" v-if="hasTransactionKamstrupUsage" style="margin-top: -0.5rem;">
                  <div class="left aligned column">
                    <b>Pris inkl. moms</b>
                  </div>
                  <div class="right aligned column">
                    <b>{{ formatPrice(transactionUnitPriceWithVat) }} / m<sup>3</sup> {{ getCurrency }}</b>
                  </div>
                </div>
              </template>

              <div class="ui divider"></div>

              <table class="ui very basic borderless compact table">
                <tbody>
                <tr v-if="hasTransactionKamstrupUsage">
                  <td class="left aligned">Vandforbrug</td>
                  <td class="right aligned">{{ transactionKamstrupMeterReadingTotal }} m<sup>3</sup></td>
                </tr>
                <tr>
                  <td class="left aligned"><b>{{ $t('components.transaction.info.total') }}</b></td>
                  <td class="right aligned">
                    <span v-if="transaction.status === 'refunded'">
                      - {{ formatPrice(amount) }} {{ getCurrency }}
                    </span>
                    <span v-else>
                      <template v-if="transaction.status === 'overdue' && subscription.product.overdue_fee_amount !== null && subscription.apply_overdue_fee">
                        {{ formatPrice(amount) }} (+ {{ formatPrice(subscription.product.overdue_fee_amount) }}) {{ getCurrency }}
                      </template>
                      <template v-else>
                        {{ formatPrice(amount) }} {{ getCurrency }}
                      </template>
                    </span>
                  </td>
                </tr>
                </tbody>
              </table>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>


<script>
import find from 'lodash/find'
import moment from 'moment'
import size from 'lodash/size'
import 'moment/locale/da'
import accounting from 'accounting'

export default {
	name: 'Transaction',

	props: {
		transaction: {
			required: true,
			type: Object
		},

		waitlist: {
			required: false,
			default: false,
			type: Boolean
		},
    amount: {
      default: null
    }
	},

	data () {
		return {
			showPaymentInfo: false,
			showLoader: false,
			subscription: null,
			selectedAccessories: []
		}
	},

	mounted () {
    this.fetchAssociatedSubscription()
	},

	computed: {
		getDateString () {
			moment.locale(window.localStorage.language || navigator.language || 'da')
			return moment(this.transaction.date).format('Do MMM, YYYY')
		},

		getTimeString () {
			moment.locale(window.localStorage.language || navigator.language || 'da')
			return moment(this.transaction.date).format('HH:mm')
		},

		getTransactionDate () {
			return moment(this.transaction.date).date()
		},

		getTransactionMonth () {
			var months = this.$t('globals.monthsshort')
			return months[moment(this.transaction.date).month()]
		},

		getTransactionYear () {
			return moment(this.transaction.date).year()
		},

		getSubscriptionName () {
			if (this.subscription.product !== null && size(this.subscription.product) > 0) {
				return this.subscription.product.name
			}

			if (this.subscription.booking !== null) {
        return this.subscription.booking.product.name
      }
		},

		getExpireDate () {
			moment.locale(window.localStorage.language || navigator.language || 'da')
			return moment(this.subscription.expire_date).format('Do MMM, YYYY')
		},

		getFailTransactionReason () {
			const chargeDescription = JSON.parse(this.transaction.chargeDescription)

      if (this.subscription.payment_method === 'mobile_pay') {
        return chargeDescription.status_text
      } else {
        if (chargeDescription.result) {
          return chargeDescription.result.message
        }
        return chargeDescription.message
      }
		},

		getCurrency () {
			if (this.subscription.pricing !== null && this.subscription.pricing.currency !== undefined && this.subscription.pricing.currency !== null) {
				return this.subscription.pricing.currency
			} else {
				return 'DKK'
			}
		},

		activeMerchant () {
			return this.$store.getters.activeMerchant
		},

		statusClass () {
			if (this.transaction.status === 'success' || this.transaction.status === 'pending') {
				return 'green'
			} else if (this.transaction.status === 'failed') {
				return 'red'
			} else if (this.transaction.status === 'refunding') {
        return 'yellow'
      } else if (this.transaction.status === 'refunded') {
				return 'blue'
			} else if (this.transaction.status === 'overdue') {
				return 'purple'
			}
		},

		statusMessage () {
			if (this.transaction.status === 'success' || this.transaction.status === 'pending') {
				if (this.transaction.type === 'manual') {
					return this.$t('components.transaction.paymentstatus.approved_manually')
				} else if (this.transaction.type === 'card') {
					return this.$t('components.transaction.paymentstatus.approved')
				}
			} else if (this.transaction.status === 'failed') {
				return this.$t('components.transaction.paymentstatus.denied')
			} else if (this.transaction.status === 'overdue') {
				return this.$t('components.transaction.paymentstatus.overdue')
			}
		},

    showDownloadPdf () {
      if (this.activeMerchant.etoken) {
        if (this.transaction.accounting_system === 'economic') {
          return this.transaction.invoice !== null && this.transaction.invoice !== '' && (this.transaction.status === 'success' || this.transaction.status === 'pending')
        }
      }
      if (this.activeMerchant.dinero) {
        if (this.transaction.accounting_system === 'dinero') {
          return this.transaction.invoice !== null && this.transaction.invoice !== '' && (this.transaction.status === 'success' || this.transaction.status === 'pending')
        }
      }

      return false
    },

    transactionKamstrupMeterReading () {
      return this.transaction && this.transaction.subscription_kamstrup_meter
        ? this.transaction.subscription_kamstrup_meter
        : null
    },

    transactionKamstrupMeterReadingTotal () {
      if (
        !this.transactionKamstrupMeterReading ||
        this.transactionKamstrupMeterReading.meter_reading_total === null
      ) {
        return 0
      }

      return parseFloat(this.transactionKamstrupMeterReading.meter_reading_total)
    },

    hasTransactionKamstrupUsage () {
      return this.transactionKamstrupMeterReadingTotal > 0
    },

    transactionUnitPriceWithVat () {
      if (!this.hasTransactionKamstrupUsage || this.amount === null) {
        return null
      }

      return (parseFloat(this.amount) / this.transactionKamstrupMeterReadingTotal)
    }
	},

	methods: {
		fetchAssociatedSubscription () {
			return new Promise((resolve, reject) => {
				var url
        if (this.transaction.wait_list_subscription_id) {
					url = `waitlistsubscriptions/${this.transaction.wait_list_subscription_id}`
				} else if (this.transaction.subscription_id) {
					url = `subscriptions/${this.transaction.subscription_id}`
				} else if (this.transaction.booked_id) {
          url = `booked/${this.transaction.booked_id}`
        }

        if (url) {
          this.axios.get(url)
            .then(({data}) => {
              if (this.transaction.booked_id) {
                this.subscription = data.data
              } else {
                this.subscription = data
              }
              resolve(data)
            }).catch(error => {
            reject(error)
          })
        }
			})
		},

		fetchAssociatedMerchant () {
			// this.axios.get()
		},

		fetchSelectedAccessories () {
			return new Promise((resolve, reject) => {
				const _this = this
				this.selectedAccessories = []

				$.map(JSON.parse(this.subscription.accessories), function (amount, accessoryId) {
					_this.selectedAccessories.push(find(_this.subscription.product.accessories, {id: parseInt(accessoryId)}))
				})

				resolve(this.selectedAccessories)
			})
		},

		loadPaymentInfo () {
      if (this.subscription.booking) return
			if (!this.showPaymentInfo) {
				this.showLoader = true
				this.fetchSelectedAccessories()
					.then(accessories => {
						this.showPaymentInfo = true
						this.showLoader = false
					})
			} else {
				this.showPaymentInfo = false
			}
		},

		downloadInvoice () {
			this.showLoader = true
			var url

			if (this.transaction.subscription_id) {
				url = `/subscriptions/transactions/${this.transaction.id}/invoice`
			} else if (this.transaction.wait_list_subscription_id) {
				url = `waitlistsubscriptions/transactions/${this.transaction.id}/invoice`
			}

			this.axios.get(url, {
				responseType: 'blob'
			})
				.then(response => {
					var blob = new Blob([response.data], { type: 'application/pdf' })
					var link = document.createElement('a')
					link.setAttribute('type', 'hidden')
					link.href = window.URL.createObjectURL(blob)

          let invoicePrefix = this.transaction.transaction_id !== '' ? this.transaction.transaction_id : 'overdue'
					link.download = 'invoice_' + invoicePrefix

					document.body.appendChild(link)
					link.click()

					this.$nextTick(() => {
						link.remove()
					})

					this.showLoader = false
				}).catch()
		},

		formatPrice (amount, minfrac = 2) {
			if (amount !== null) {
				return accounting.format(amount, minfrac)
			}
		},

    getProductPriceFromTotalPrice (subscription, transaction, amount) {
      let meterReadingTotal = this.transactionKamstrupMeterReadingTotal

      if (meterReadingTotal > 0) {
        amount = amount / meterReadingTotal
      }

      if (subscription.accessories === 'null') {
        if (subscription.product.apply_vat) {
          return this.formatPrice(amount * 0.8)
        } else {
          return this.formatPrice(amount)
        }
      } else {
        let totalAccessoriesPrice = 0
        this.selectedAccessories.forEach((value, index) => {
          let accessoryPricing = value.pricing
          if (accessoryPricing.length > 0) {
            accessoryPricing.forEach((value, index) => {
              if (value.interval === subscription.pricing.interval) {
                totalAccessoriesPrice += value.price
              }
            })
          }
        })
        let price = amount - totalAccessoriesPrice
        if (subscription.product.apply_vat) {
          return this.formatPrice(price * 0.8)
        } else {
          return this.formatPrice(price)
        }
      }
    },

    getProductPriceVatFromTotalPrice (subscription, transaction, amount) {
      let meterReadingTotal = this.transactionKamstrupMeterReadingTotal

      if (meterReadingTotal > 0) {
        amount = amount / meterReadingTotal
      }

      if (subscription.accessories === 'null') {
        return this.formatPrice(amount - (amount * 0.8))
      } else {
        let totalAccessoriesPrice = 0
        this.selectedAccessories.forEach((value, index) => {
          let accessoryPricing = value.pricing
          if (accessoryPricing.length > 0) {
            accessoryPricing.forEach((value, index) => {
              if (value.interval === subscription.pricing.interval) {
                totalAccessoriesPrice += value.price
              }
            })
          }
        })
        let price = amount - totalAccessoriesPrice
        let vatPrice = price - (price * 0.8)
        return this.formatPrice(vatPrice)
      }
    },

    getAccessoryPrice (accessory) {
      let accessoryPricing = accessory.pricing
      let isVatApplied = accessory.apply_vat
      if (accessoryPricing.length > 0) {
        let accessoryPrice = 0
        accessoryPricing.forEach((value, index) => {
          if (value.interval === this.subscription.pricing.interval) {
            accessoryPrice += value.price
          }
        })
        if (isVatApplied) {
          return this.formatPrice(accessoryPrice * 0.8)
        } else {
          return this.formatPrice(accessoryPrice)
        }
      }
      return 0
    },

    getAccessoryPriceVat (accessory) {
      let accessoryPricing = accessory.pricing
      if (accessoryPricing.length > 0) {
        let accessoryPrice = 0
        accessoryPricing.forEach((value, index) => {
          if (value.interval === this.subscription.pricing.interval) {
            accessoryPrice += value.price
          }
        })
        return this.formatPrice(accessoryPrice - (accessoryPrice * 0.8))
      }
      return 0
    }
	}
}
</script>



// WEBPACK FOOTER //
// src/components/payment/Transaction.vue